import { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@center-pwa/store/store';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { fetchIdentity } from '@center-pwa/reducers/userProfileReducer';
import type { AppDispatch } from '@center-pwa/store/store';

export type Theme = 'light' | 'dark' | 'system';

export interface NotificationChannels {
  inApp: boolean;
  email: boolean;
  sms: boolean;
}

interface PreferencesUpdateResponse {
  status: string;
  message: string;
  data: {
    identityId: string;
    preferences: Record<string, any>;
  };
}

interface UseIdentityPreferencesReturn {
  preferences: Record<string, any>;
  theme: Theme;
  /** Typed view of `preferences.notifications.notificationChannels`, defaulted per the backend's own defaults (SMS opt-in only). */
  notificationChannels: NotificationChannels;
  isLoading: boolean;
  error: string | null;
  updatePreferences: (updates: Record<string, any>) => Promise<boolean>;
  setTheme: (theme: Theme) => Promise<boolean>;
}

/**
 * Hook for managing user identity preferences stored on the server
 */
export const useIdentityPreferences = (): UseIdentityPreferencesReturn => {
  const dispatch = useDispatch<AppDispatch>();
  const apiClients = useApiClientsOrNull();
  
  const { identity, fetchIdentityStatus, fetchIdentityError } = useSelector((state: RootState) => state.userProfile);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  // Get current preferences from identity, with fallback to localStorage for migration
  const preferences = identity?.identity?.preferences || {};
  
  // Get theme preference with fallbacks
  const getThemePreference = useCallback((): Theme => {
    // 1. Check server preferences first
    if (preferences.theme && ['light', 'dark', 'system'].includes(preferences.theme)) {
      return preferences.theme as Theme;
    }
    
    // 2. Fallback to localStorage for migration (will be synced to server on first update)
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('center-app-theme-preference');
      if (stored && ['light', 'dark', 'system'].includes(stored)) {
        return stored as Theme;
      }
    }
    
    // 3. Default to system preference
    return 'system';
  }, [preferences.theme]);

  const theme = getThemePreference();

  const notificationChannels: NotificationChannels = {
    inApp: preferences.notifications?.notificationChannels?.inApp ?? true,
    email: preferences.notifications?.notificationChannels?.email ?? true,
    sms: preferences.notifications?.notificationChannels?.sms ?? false,
  };

  // Update preferences on the server
  const updatePreferences = useCallback(async (updates: Record<string, any>): Promise<boolean> => {
    if (!apiClients) {
      console.error('Cannot update preferences: API clients not available');
      setUpdateError('Unable to update preferences - authentication required');
      return false;
    }

    if (!identity?.identity?.id) {
      console.error('Cannot update preferences: missing identity');
      setUpdateError('Unable to update preferences - user identity not available');
      return false;
    }

    setIsUpdating(true);
    setUpdateError(null);

    try {
      console.log(`🎨 Updating preferences for identity ${identity.identity.id}:`, updates);
      
      const response = await apiClients.graphClient.patch<PreferencesUpdateResponse>(
        `/identities/${identity.identity.id}/preferences`,
        updates
      );

      if (response.status === 'success') {
        console.log('✅ Preferences updated successfully:', response.data.preferences);
        
        // Refresh the identity data to get updated preferences  
        await dispatch(fetchIdentity({
          identityId: identity.identity.id,
          apiClient: apiClients.graphClient
        }));
        
        setUpdateError(null);
        return true;
      } else {
        throw new Error('Server returned non-success status');
      }
    } catch (error: any) {
      console.error('❌ Failed to update preferences:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update preferences';
      setUpdateError(errorMessage);
      return false;
    } finally {
      setIsUpdating(false);
    }
  }, [identity?.identity?.id, apiClients, dispatch]);

  // Convenience method for setting theme preference
  const setTheme = useCallback(async (newTheme: Theme): Promise<boolean> => {
    console.log(`🎨 Setting theme to: ${newTheme}`);
    
    // Update localStorage immediately for responsive UI
    if (typeof window !== 'undefined') {
      localStorage.setItem('center-app-theme-preference', newTheme);
    }
    
    // Update server preferences
    return await updatePreferences({ theme: newTheme });
  }, [updatePreferences]);

  return {
    preferences,
    theme,
    notificationChannels,
    isLoading: fetchIdentityStatus === 'loading' || isUpdating,
    error: updateError || fetchIdentityError,
    updatePreferences,
    setTheme
  };
};